import { t } from '@sm/intl';
import { CountryCodes } from '@sm/locale/dist/localeSets';
import { StaticContext } from '@sm/webassets';
import React, { useContext, useMemo } from 'react';

import { MONTHLY_PACKAGES, TEAMS_PACKAGES } from '~pricing/lib/constants';
import {
  formatCostInLocaleCurrency,
  formatCostInSkuCurrency,
  formatMonthlyCostInLocaleCurrency,
  getCoreCostReplicaForBasicPackage,
  getDiscountedSkuCost,
  getSkuCostByType,
} from '~pricing/lib/lib-helpers';
import { Packages, SkuTypes } from '~shared/constants/pricing';

import { PackageSkuCosts } from '~shared/types/pricing/package';
import { PriceDisplayHook } from '../types';

import { EXPERIMENT_IDENTIFIERS } from '~server/routes/Pricing/experimentsConfig';
import { usePackages } from '~shared/hooks/usePackages';
import { usePricingExperiments } from '~shared/hooks/usePricingExperiments';
import { DiscountInfo } from '~shared/types/pricing/pricingsvc';
import COPY from './copy';

const MINIMUM_EXTRA_TEAM_SEATS = 2;

export default function usePriceDisplay(
  packageName: Packages,
  skuCosts: PackageSkuCosts,
  discountInfo?: DiscountInfo
): PriceDisplayHook {
  const {
    environment: { countryCode },
  } = useContext(StaticContext);
  const { displayPackages } = usePackages();
  const isBasic = useMemo(() => packageName === Packages.Basic, [packageName]);
  const isBrazil = useMemo(() => countryCode === CountryCodes.BR, [countryCode]);
  const isMonthlyPackage = useMemo(() => MONTHLY_PACKAGES.includes(packageName), [packageName]);
  const isTeamsPackage = useMemo(() => TEAMS_PACKAGES.includes(packageName), [packageName]);
  const isGrow2529FormsMonthlyPackage = useMemo(
    () => [Packages.StandardMonthly, Packages.FormsMonthly].includes(packageName),
    [packageName]
  );

  const { uiExperiment, priceExperiment } = usePricingExperiments();
  const isGrow2529Forms =
    uiExperiment.hasAssignment && uiExperiment.experimentName === EXPERIMENT_IDENTIFIERS.GROW_2529_FORMS.name;

  const isPnPRefresh =
    priceExperiment.hasAssignment && priceExperiment.treatmentName === EXPERIMENT_IDENTIFIERS.GROW_2792.treatment;

  const [coreSeatCost, additionalSeatCost, discountedCoreSeatCost, discountedAdditionalSeatCost] = useMemo(() => {
    // fake a SKU for basic
    const coreSeatSku = isBasic
      ? getCoreCostReplicaForBasicPackage(displayPackages)
      : getSkuCostByType(skuCosts, SkuTypes.CoreSeat);
    const additionalSeatSku = isBasic ? coreSeatSku : getSkuCostByType(skuCosts, SkuTypes.AdditionalSeat);

    const discountedCoreSeatSku = coreSeatSku && discountInfo && getDiscountedSkuCost(coreSeatSku, discountInfo);

    const discountedAdditionalSeatSku =
      additionalSeatSku && discountInfo && getDiscountedSkuCost(additionalSeatSku, discountInfo);

    return [coreSeatSku, additionalSeatSku, discountedCoreSeatSku, discountedAdditionalSeatSku];
  }, [isBasic, skuCosts, displayPackages, discountInfo]);

  const monthlyFormattedCost = useMemo(
    () =>
      coreSeatCost &&
      (isMonthlyPackage ? formatCostInLocaleCurrency(coreSeatCost) : formatMonthlyCostInLocaleCurrency(coreSeatCost)),
    [isMonthlyPackage, coreSeatCost]
  );

  const annualFormattedCost = useMemo(
    () =>
      coreSeatCost &&
      (isMonthlyPackage
        ? formatCostInSkuCurrency((coreSeatCost.cost / 100) * 12, coreSeatCost)
        : formatCostInLocaleCurrency(coreSeatCost)),
    [isMonthlyPackage, coreSeatCost]
  );

  const monthlyFormattedDiscountedCost = useMemo(
    () =>
      discountedCoreSeatCost &&
      (isMonthlyPackage
        ? formatCostInLocaleCurrency(discountedCoreSeatCost)
        : formatMonthlyCostInLocaleCurrency(discountedCoreSeatCost)),
    [isMonthlyPackage, discountedCoreSeatCost]
  );

  const annualFormattedDiscountedCost = useMemo(
    () =>
      discountedCoreSeatCost &&
      (isMonthlyPackage
        ? formatCostInSkuCurrency((discountedCoreSeatCost.cost / 100) * 12, discountedCoreSeatCost)
        : formatCostInLocaleCurrency(discountedCoreSeatCost)),
    [isMonthlyPackage, discountedCoreSeatCost]
  );

  const annualTeamsFormattedCost = useMemo(() => {
    let retVal: React.ReactElement | null | string = null;

    if (isTeamsPackage && coreSeatCost && additionalSeatCost) {
      // a base teams cost will be one core seat + two additional seats
      // I don't know if these two seat types have ever had different values,
      // but some day they will... some day...
      const baseCost = Number(coreSeatCost.cost) + Number(additionalSeatCost.cost) * MINIMUM_EXTRA_TEAM_SEATS;
      retVal = formatCostInSkuCurrency(baseCost / 100, coreSeatCost);
    }

    return retVal;
  }, [isTeamsPackage, coreSeatCost, additionalSeatCost]);

  const annualTeamsFormattedDiscountedCost = useMemo(() => {
    let retVal: React.ReactElement | null | string = null;

    if (isTeamsPackage && discountedCoreSeatCost && discountedAdditionalSeatCost) {
      // a base teams cost will be one core seat + two additional seats
      // I don't know if these two seat types have ever had different values,
      // but some day they will... some day...
      const baseCost =
        Number(discountedCoreSeatCost.cost) + Number(discountedAdditionalSeatCost.cost) * MINIMUM_EXTRA_TEAM_SEATS;
      retVal = formatCostInSkuCurrency(baseCost / 100, discountedCoreSeatCost);
    }

    return retVal;
  }, [isTeamsPackage, discountedCoreSeatCost, discountedAdditionalSeatCost]);

  const headerCost = useMemo(() => {
    // monthly packages always display the monthly cost, regardless of geo (at this point in time)
    if (isMonthlyPackage) {
      return monthlyFormattedCost;
    }

    // brazil always displays a calculated annual cost
    if (isBrazil) {
      return isTeamsPackage ? annualTeamsFormattedCost : annualFormattedCost;
    }

    // for everything else, monthly
    return monthlyFormattedCost;
  }, [annualTeamsFormattedCost, annualFormattedCost, monthlyFormattedCost, isBrazil, isMonthlyPackage, isTeamsPackage]);

  const headerDiscountedCost = useMemo(() => {
    // monthly packages always display the monthly cost, regardless of geo (at this point in time)
    // Intentionally returning null value for monthly packages, as discounts don't apply
    if (isMonthlyPackage) {
      return null;
    }

    // brazil always displays a calculated annual cost
    if (isBrazil) {
      return isTeamsPackage ? annualTeamsFormattedDiscountedCost : annualFormattedDiscountedCost;
    }

    // for everything else, monthly
    return monthlyFormattedDiscountedCost;
  }, [
    annualTeamsFormattedDiscountedCost,
    annualFormattedDiscountedCost,
    monthlyFormattedDiscountedCost,
    isBrazil,
    isMonthlyPackage,
    isTeamsPackage,
  ]);

  const headerUnit = useMemo(() => {
    if (isBrazil || isBasic) {
      return '';
    }

    if (isTeamsPackage || isPnPRefresh) {
      return t(COPY.PER_USER_PER_MONTH);
    }

    return t(COPY.PER_MONTH);
  }, [isBrazil, isBasic, isTeamsPackage, isPnPRefresh]);

  const headerByline = useMemo(() => {
    // monthly packages have no byline
    if (isMonthlyPackage && !isGrow2529Forms && !isPnPRefresh) {
      return '';
    }

    if (isMonthlyPackage && isPnPRefresh) {
      return t(COPY.BILLED_MONTHLY);
    }

    // basic is also special
    if (isBasic) {
      return t(COPY.ALWAYS_FREE);
    }

    // Check to see if there's a package specific byline
    const packageCopyKey = `${packageName?.toUpperCase()}_BYLINE` as keyof typeof COPY;
    if (packageCopyKey in COPY) {
      return t(COPY[packageCopyKey]);
    }

    // GROW-2529 FORMS
    if (isGrow2529Forms && isGrow2529FormsMonthlyPackage) {
      return t(COPY.BILLED_MONTHLY);
    }

    // fallback to default text
    const copyKey = `${isTeamsPackage ? 'TEAMS_' : ''}BILLED_ANNUALLY` as keyof typeof COPY;

    // use the COPY file to check to see if there's a country specific byline
    const countryOverrideCopyKey = `${countryCode.toUpperCase()}_${copyKey}` as keyof typeof COPY;
    if (countryOverrideCopyKey in COPY) {
      return t(COPY[countryOverrideCopyKey]);
    }

    return t(COPY[copyKey], { price: annualFormattedCost });
  }, [
    isMonthlyPackage,
    isGrow2529Forms,
    isBasic,
    packageName,
    isGrow2529FormsMonthlyPackage,
    isPnPRefresh,
    isTeamsPackage,
    countryCode,
    annualFormattedCost,
  ]);

  return {
    isMonthlyPackage,
    isTeamsPackage,
    annualFormattedCost,
    monthlyFormattedCost,
    monthlyFormattedDiscountedCost,
    annualTeamsFormattedCost,
    annualFormattedDiscountedCost,
    annualTeamsFormattedDiscountedCost,
    headerCost,
    headerDiscountedCost,
    headerUnit,
    headerByline,
  };
}
